import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AIAuditTryoutService } from "Services/axios/AIAuditTryout.Service";
import { handleApiError } from "Features/Common/ErrorHandler";

// Map field verified_by_AI dari response GET ke status lokal.
// none -> not_audited | process -> auditing | done -> done | failed -> error
const mapVerifiedByAIToStatus = (verifiedByAI) => {
  switch (verifiedByAI) {
    case "none":
      return "not_audited";
    case "process":
      return "auditing";
    case "done":
      return "done";
    case "failed":
      return "error";
    default:
      return "not_audited";
  }
};

const normalizeSolvedValue = (value) => {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  if (value === 0 || value === "0" || value === "false") return false;
  return Boolean(value);
};

const buildStatisticsFromResults = (results = [], baseStatistics = {}) => {
  const nextStatistics = { ...(baseStatistics ?? {}) };
  const solvedCount = results.filter((result) => normalizeSolvedValue(result?.is_solved)).length;
  const unsolvedCount = results.length - solvedCount;

  nextStatistics.total_solved = solvedCount;
  nextStatistics.total_unsolved = unsolvedCount;

  if (typeof nextStatistics.total_questions !== "number") {
    nextStatistics.total_questions = results.length;
  }

  return nextStatistics;
};

// Mulai audit AI untuk satu tryout.
  // Expect 202 -> status 'auditing'.
  // Optional: confirmReaudit=true untuk audit ulang (hapus hasil lama).
  export const startAuditThunk = createAsyncThunk(
    "AIAudit/startAudit",
    async ({ tryoutId, confirmReaudit = false }, { rejectWithValue }) => {
      try {
        const response = await AIAuditTryoutService.startAudit(tryoutId, confirmReaudit);
        return response.data;
      } catch (error) {
        handleApiError(error);
        return rejectWithValue(
          error?.response?.data?.message || error?.message || "Gagal memulai audit AI"
        );
      }
    }
  );

// Ambil hasil audit AI untuk satu tryout.
// GET response: { data: { id, title, verified_by_AI, AI_summary, AI_error_stage,
//   AI_error_message, statistics: {...}, audit_results: [...] } }
export const getAuditResultsThunk = createAsyncThunk(
  "AIAudit/getAuditResults",
  async (tryoutId, { rejectWithValue }) => {
    try {
      const response = await AIAuditTryoutService.getAuditResults(tryoutId);
      return response.data;
    } catch (error) {
      handleApiError(error);
      return rejectWithValue(
        error?.response?.data?.message || error?.message || "Gagal mengambil hasil audit"
      );
    }
  }
);

// Tandai satu hasil audit (ai_question_audit_results) sebagai solved/unsolved.
// PATCH /api/feature/membership/tryout/ai-audit-results/{id}/solve
// arg: { id (ai_question_audit_results id), isSolved: boolean }
export const solveResultThunk = createAsyncThunk(
  "AIAudit/solveResult",
  async ({ id, isSolved }, { rejectWithValue }) => {
    try {
      const response = await AIAuditTryoutService.solveAuditResult(id, {
        is_solved: isSolved,
      });
      return { id, isSolved, data: response?.data };
    } catch (error) {
      handleApiError(error);
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        "Gagal memperbarui status hasil audit"
      );
    }
  }
);

const aiAuditSlice = createSlice({
  name: "AIAudit",
  initialState: {
    // status: idle | not_audited | auditing | done | error
    status: "idle",
    results: [], // array audit_results
    summary: null, // AI_summary
    errorStage: null, // AI_error_stage
    errorMessage: null, // AI_error_message
    statistics: null, // object statistics
    verifiedByAI: null, // raw field verified_by_AI dari response
    isLoading: false, // loading saat mengambil hasil
    isStarting: false, // loading saat memulai audit
    error: null, // error generik (network/reject)
    title: null, // tryout title dari response GET
  },
  reducers: {
    // Reset state audit (berguna saat pindah tryout / keluar halaman).
    resetAIAudit: (state) => {
      state.status = "idle";
      state.results = [];
      state.summary = null;
      state.errorStage = null;
      state.errorMessage = null;
      state.statistics = null;
      state.verifiedByAI = null;
      state.isLoading = false;
      state.isStarting = false;
      state.error = null;
      state.title = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ---- startAuditThunk ----
      .addCase(startAuditThunk.pending, (state) => {
        state.isStarting = true;
        state.error = null;
      })
      .addCase(startAuditThunk.fulfilled, (state, action) => {
        state.isStarting = false;
        state.status = "auditing";
      })
      .addCase(startAuditThunk.rejected, (state, action) => {
        state.isStarting = false;
        state.status = "error";
        state.error =
          action.error?.message || action.payload || "Gagal memulai audit AI";
      })
      // ---- getAuditResultsThunk ----
      .addCase(getAuditResultsThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAuditResultsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        const data = action.payload?.data;
        const nextResults = data?.audit_results ?? [];

        // BUG FIX: ambil array audit_results, bukan seluruh objek tryout.
        state.results = nextResults;

        // Field tambahan dari response.
        state.summary = data?.AI_summary ?? null;
        state.errorStage = data?.AI_error_stage ?? null;
        state.errorMessage = data?.AI_error_message ?? null;
        state.statistics = buildStatisticsFromResults(nextResults, data?.statistics ?? {});
        state.verifiedByAI = data?.verified_by_AI ?? null;
        state.title = data?.title ?? null; // tryout title

        // Status diturunkan dari verified_by_AI, bukan di-hardcode 'done'.
        state.status = mapVerifiedByAIToStatus(data?.verified_by_AI);
      })
      .addCase(getAuditResultsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.status = "error";
        state.error =
          action.error?.message || action.payload || "Gagal mengambil hasil audit";
      })
      // ---- solveResultThunk ----
      .addCase(solveResultThunk.fulfilled, (state, action) => {
        const { id, isSolved, data } = action.payload;
        // Response solve berisi object audit_result yang sudah terupdate.
        // Bisa dibungkus { data: {...} } (sama pola GET) atau langsung object-nya.
        const updated = data?.data ?? data;
        const target = state.results.find((r) => r.id === id);
        if (target) {
          if (updated) {
            // Simpan field relasi sebelum di-overwrite, jaga-jaga kalau
            // response solve tidak mengembalikannya.
            const preservedQuestion = target.question;
            const preservedCategory = target.question_category;
            // Merge seluruh field agar solved_by, solved_at, is_solved, dll ikut terbarui.
            Object.assign(target, updated);
            if (!target.question && preservedQuestion) {
              target.question = preservedQuestion;
            }
            if (!target.question_category && preservedCategory) {
              target.question_category = preservedCategory;
            }
          } else {
            // Fallback kalau response kosong: tetap update flag is_solved.
            target.is_solved = isSolved;
          }
        }

        if (state.statistics) {
          state.statistics = buildStatisticsFromResults(state.results, state.statistics);
        }

        state.error = null;
      })
      .addCase(solveResultThunk.rejected, (state, action) => {
        state.error =
          action.error?.message ||
          action.payload ||
          "Gagal memperbarui status hasil audit";
      });
  },
});

export const { resetAIAudit } = aiAuditSlice.actions;

export default aiAuditSlice.reducer;